import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  PiSwordDuotone,
  PiTrashDuotone,
  PiArrowClockwiseDuotone,
  PiArrowRightDuotone,
  PiCheckCircleDuotone,
  PiPencilSimpleDuotone,
  PiSparkleDuotone,
} from 'react-icons/pi';
import { getChallenges, deleteChallenge, recalculateMainScores } from '../api/challengeService';
import { getLeague } from '../api/leagueService';
import { getRoom } from '../api/roomService';
import { ACCESS_DENIED_MESSAGE, accessAwareMessage } from '../api/api';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Badge from '../shared/common/Badge';
import Breadcrumb from '../shared/common/Breadcrumb';
import EmptyState from '../shared/common/EmptyState';
import { ConfirmModal } from '../shared/common/Modal';
import { SkeletonGrid } from '../shared/common/Skeleton';
import ScoringRulesSummary from '../shared/business/scoring/ScoringRulesSummary';
import { useAuth } from '../app/providers/AuthContext';
import { useDelayedLoading } from '../shared/utils/useDelayedLoading';
import { appendChallengePrefillSearch, readChallengePrefillSearch } from '../shared/utils/challengeCreationFlow';
import { getSideBetIndicator } from '../shared/business/challenge/sideBetIndicator';

const STATUS_MAP = { open: 'open', closed: 'closed', resolved: 'resolved' };
const STATUS_LABELS = { open: 'приём ставок', closed: 'ждём результат', resolved: 'завершён' };
const RESULT_LABELS = { WON: 'выигран', LOST: 'не выигран', PENDING: 'ожидает результата' };
const RESULT_BADGE_STATUS = { WON: 'open', LOST: 'resolved', PENDING: 'closed' };
const OWNED_CHALLENGE_BADGE_LABEL = 'Моё пари';
const STAT_EMPTY_VALUE = 0;
const RECALCULATE_SUCCESS_MESSAGE = 'Очки пересчитаны';
const RECALCULATE_ERROR_MESSAGE = 'Не удалось пересчитать очки';
const RECALCULATE_BUTTON_TITLE = 'Пересчитать очки и событие';
const CHALLENGE_DATE_FALLBACK_TIMESTAMP = Number.MAX_SAFE_INTEGER;
const CHALLENGE_ID_FALLBACK = Number.MAX_SAFE_INTEGER;

const getChallengeEventTimestamp = (challenge) => {
  const timestamp = Date.parse(challenge?.eventStartDate || '');
  return Number.isNaN(timestamp) ? CHALLENGE_DATE_FALLBACK_TIMESTAMP : timestamp;
};

const getChallengeStableId = (challenge) => Number(challenge?.id ?? CHALLENGE_ID_FALLBACK);

const sortChallengesByEventDate = (items) => [...items].sort((left, right) => {
  const dateDiff = getChallengeEventTimestamp(right) - getChallengeEventTimestamp(left);
  if (dateDiff !== 0) return dateDiff;
  return getChallengeStableId(left) - getChallengeStableId(right);
});

const buildChallengeListPath = (roomId, leagueId) => (
  leagueId ? `/rooms/${roomId}/leagues/${leagueId}/challenges` : `/rooms/${roomId}/challenges`
);

const buildChallengeCreatePath = (roomId, leagueId) => `${buildChallengeListPath(roomId, leagueId)}/create`;

const buildChallengeViewPath = (roomId, leagueId, challengeId) => (
  `${buildChallengeListPath(roomId, leagueId)}/${challengeId}`
);

const buildChallengeEditPath = (roomId, leagueId, challengeId) => (
  `${buildChallengeViewPath(roomId, leagueId, challengeId)}/edit`
);

const isResolvedStatus = (status) => status === 'RESOLVED' || status === 'resolved';
const formatStatusLabel = (status) => STATUS_LABELS[status?.toLowerCase?.()] || 'приём ставок';
const formatResultLabel = (result) => RESULT_LABELS[result] || RESULT_LABELS.PENDING;
const resultBadgeStatus = (result) => RESULT_BADGE_STATUS[result] || RESULT_BADGE_STATUS.PENDING;
const challengeScore = (challenge) => Number(challenge?.currentUserScore ?? STAT_EMPTY_VALUE);
const getCurrentUserId = (user) => user?.id ?? user?.userId;
const isCreatedByCurrentUser = (challenge, currentUserId) => (
  challenge?.creatorId != null && currentUserId != null && String(challenge.creatorId) === String(currentUserId)
);

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('ru-RU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatChallengePeriod = (challenge) => {
  const start = formatDate(challenge?.eventStartDate);
  const end = formatDate(challenge?.eventEndDate);
  if (start && end) return `${start} — ${end}`;
  return start || end;
};

const SideBetIndicator = ({ challenge }) => {
  const indicator = getSideBetIndicator(challenge);
  if (!indicator) return null;

  return (
    <span
      className="bm-text-sm"
      title={indicator.title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '3px 8px',
        borderRadius: 999,
        border: `1px solid ${indicator.border}`,
        background: indicator.background,
        color: indicator.accent,
        fontWeight: 700,
      }}
    >
      <PiSparkleDuotone size={14} />
      {indicator.label}
    </span>
  );
};

export default function ChallengePage() {
  const { roomId, leagueId } = useParams();
  const [room, setRoom] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const showSkeleton = useDelayedLoading(loading);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [recalculatingChallengeId, setRecalculatingChallengeId] = useState(null);
  const [recalculateMessage, setRecalculateMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const currentUserId = getCurrentUserId(user);
  const challengePrefill = useMemo(() => readChallengePrefillSearch(location.search), [location.search]);

  const isLeagueScope = Boolean(leagueId);
  const challengeCreatePath = appendChallengePrefillSearch(buildChallengeCreatePath(roomId, leagueId), challengePrefill);
  const roomLabel = room?.name || 'Комната';
const scopeTitle = 'Пари комнаты';
  const activeChallenges = useMemo(
    () => sortChallengesByEventDate(challenges.filter((challenge) => !isResolvedStatus(challenge.status))),
    [challenges],
  );
  const resolvedChallenges = useMemo(
    () => sortChallengesByEventDate(challenges.filter((challenge) => isResolvedStatus(challenge.status))),
    [challenges],
  );
  const breadcrumbItems = [
    { label: 'Комнаты', to: '/rooms' },
    { label: roomLabel, to: `/rooms/${roomId}` },
    { label: 'Пари' },
  ];

  const loadPageData = useCallback(() => {
    setLoading(true);
    const leagueRequest = isLeagueScope ? getLeague(roomId, leagueId) : Promise.resolve(null);

    Promise.all([getRoom(roomId), leagueRequest, getChallenges(roomId, leagueId)])
      .then(([roomData, _leagueData, challengesData]) => {
        setRoom(roomData);
        setChallenges(challengesData);
      })
      .catch((err) => setError(accessAwareMessage(err, 'Не удалось загрузить данные')))
      .finally(() => setLoading(false));
  }, [roomId, leagueId, isLeagueScope]);

  useEffect(() => {
document.title = 'BetMate — Пари комнаты';
    loadPageData();
  }, [isLeagueScope, loadPageData]);

  const handleDelete = async () => {
    if (!canManageChallenge(deleteTarget)) return;
    setDeleting(true);
    try {
      await deleteChallenge(roomId, leagueId, deleteTarget.id);
      setChallenges((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
setError(accessAwareMessage(err, 'Не удалось удалить пари'));
    } finally {
      setDeleting(false);
    }
  };

  const handleRecalculateMainScores = async (event, challenge) => {
    event.stopPropagation();
    if (!canRecalculateChallenge(challenge)) return;
    setError('');
    setRecalculateMessage('');
    setRecalculatingChallengeId(challenge.id);
    try {
      const report = await recalculateMainScores(roomId, leagueId, challenge.id);
      await refreshChallenges();
      setRecalculateMessage(formatRecalculateMessage(report));
    } catch (err) {
      setError(accessAwareMessage(err, RECALCULATE_ERROR_MESSAGE));
    } finally {
      setRecalculatingChallengeId(null);
    }
  };

  if (!loading && error === ACCESS_DENIED_MESSAGE) return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb
        items={[
          { label: 'Комнаты', to: '/rooms' },
          { label: 'Комната недоступна' },
        ]}
      />
      <div className="bm-auth-error">{error}</div>
    </div>
  );

  return (
    <div className="bm-animate-in" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-xl)' }}>
      <Breadcrumb items={breadcrumbItems} />

{/* Описание области пари */}
      <div>
        <h1 className="bm-h2">{scopeTitle}</h1>
        <p className="bm-text-secondary" style={{ marginTop: 4 }}>
          Пари, созданные в этой комнате.
        </p>
      </div>

{/* Секция пари */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
<h2 className="bm-h3">Пари</h2>
        <Button
          variant="primary"
          icon={<PiSwordDuotone size={16} />}
onClick={() => navigate(challengeCreatePath)}
        >
          Создать пари
        </Button>
      </div>

      {error && <div className="bm-auth-error">{error}</div>}
      {recalculateMessage && <div className="bm-auth-success">{recalculateMessage}</div>}

      {showSkeleton ? (
        <SkeletonGrid count={4} />
      ) : challenges.length === 0 ? (
        <EmptyState
          icon={<PiSwordDuotone size={40} />}
title="Пари пока нет"
hint="Создай первое пари в комнате"
actionLabel="Создать пари"
          onAction={() => navigate(challengeCreatePath)}
        />
      ) : (
        <>
          <div className="bm-grid">
{/* Активные пари (OPEN / CLOSED) */}
            {activeChallenges.map((challenge) => {
              const isOwner = isCreatedByCurrentUser(challenge, currentUserId);
              const canManage = canManageChallenge(challenge);

              return (
                <Card
                  key={challenge.id}
                  variant="interactive"
                  onClick={() => navigate(buildChallengeViewPath(roomId, leagueId, challenge.id))}
                  style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Badge status={STATUS_MAP[challenge.status?.toLowerCase()] || 'primary'} pulse={challenge.status === 'OPEN' || challenge.status === 'open'}>
                        {formatStatusLabel(challenge.status)}
                      </Badge>
                      {challenge.currentUserBet && (
                        <PiCheckCircleDuotone size={16} style={{ color: 'var(--bm-accent-green)' }} title="Вы сделали основную ставку" />
                      )}
                      <SideBetIndicator challenge={challenge} />
                      {isOwner && (
                        <Badge status="primary">{OWNED_CHALLENGE_BADGE_LABEL}</Badge>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<PiPencilSimpleDuotone size={14} />}
                          onClick={(e) => { e.stopPropagation(); navigate(buildChallengeEditPath(roomId, leagueId, challenge.id)); }}
                          style={{ color: 'var(--bm-accent-primary)', flexShrink: 0 }}
                        />
                      )}
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={<PiTrashDuotone size={14} />}
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(challenge); }}
                          style={{ color: 'var(--bm-accent-red)', flexShrink: 0 }}
                        />
                      )}
                    </div>
                  </div>
                  <h4 className="bm-h4">
                    {challenge.eventTitle || `Событие #${challenge.eventId}`}
                  </h4>
                  {formatChallengePeriod(challenge) && (
                    <p className="bm-text-secondary bm-text-sm">
                      Период: {formatChallengePeriod(challenge)}
                    </p>
                  )}
                  <Badge status={resultBadgeStatus(challenge.currentUserResult)}>
                    {formatResultLabel(challenge.currentUserResult)}
                  </Badge>
                  <ScoringRulesSummary scoringRules={challenge.scoringRules} compact />
                </Card>
              );
            })}
          </div>

          {/* Завершённые (история) */}
          {resolvedChallenges.length > 0 && (
            <>
              <h2 className="bm-h3" style={{ marginTop: 'var(--bm-space-lg)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <PiCheckCircleDuotone size={20} style={{ color: 'var(--bm-accent-green)' }} />
                История
              </h2>
              <div className="bm-grid">
                {resolvedChallenges.map((challenge) => {
                  const isOwner = isCreatedByCurrentUser(challenge, currentUserId);
                  const canRecalculate = canRecalculateChallenge(challenge);
                  const isRecalculating = recalculatingChallengeId === challenge.id;

                  return (
                    <Card
                      key={challenge.id}
                      variant="interactive"
                      onClick={() => navigate(buildChallengeViewPath(roomId, leagueId, challenge.id))}
                      style={{
                        display: 'flex', flexDirection: 'column', gap: 10,
                        opacity: 0.75,
                        border: '1px solid rgba(0,214,143,0.2)',
                        background: 'rgba(0,214,143,0.03)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <Badge status="resolved">
                            {formatStatusLabel(challenge.status)}
                          </Badge>
                          <SideBetIndicator challenge={challenge} />
                          {isOwner && (
                            <Badge status="primary">{OWNED_CHALLENGE_BADGE_LABEL}</Badge>
                          )}
                        </div>
                        {challenge.outcome && (
                          <span className="bm-text-sm" style={{ color: 'var(--bm-accent-green)', fontWeight: 600 }}>
                            {challenge.outcome}
                          </span>
                        )}
                      </div>
                      <h4 className="bm-h4">
                        {challenge.eventTitle || `Событие #${challenge.eventId}`}
                      </h4>
                      {formatChallengePeriod(challenge) && (
                        <p className="bm-text-secondary bm-text-sm">
                          Период: {formatChallengePeriod(challenge)}
                        </p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Badge status={resultBadgeStatus(challenge.currentUserResult)}>
                          {formatResultLabel(challenge.currentUserResult)}
                        </Badge>
                        <span className="bm-text-sm" style={{ color: challengeScore(challenge) > 0 ? 'var(--bm-accent-green)' : 'var(--bm-text-muted)', fontWeight: 700 }}>
                          {challengeScore(challenge)} очк.
                        </span>
                      </div>
                      <ScoringRulesSummary scoringRules={challenge.scoringRules} compact />
                      <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid var(--bm-border)' }}>
                        <Button
                          variant="secondary"
                          size="sm"
                          icon={<PiArrowRightDuotone size={14} />}
                          onClick={(e) => { e.stopPropagation(); navigate(buildChallengeViewPath(roomId, leagueId, challenge.id)); }}
                          style={{ width: '100%' }}
                        >
                          Посмотреть результаты
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
title="Удалить пари"
        message="Удалить это пари? Все ставки будут потеряны."
      />
    </div>
  );

  async function refreshChallenges() {
    const challengesData = await getChallenges(roomId, leagueId);
    setChallenges(challengesData);
  }

  function canManageChallenge(challenge) {
    return challenge?.editable === true && challenge?.managementAvailable === true;
  }

  function canRecalculateChallenge(challenge) {
    return isResolvedStatus(challenge?.status) && challenge?.managementAvailable === true;
  }

  function formatRecalculateMessage(report) {
    if (!report) return RECALCULATE_SUCCESS_MESSAGE;
    return `${RECALCULATE_SUCCESS_MESSAGE}: изменено ставок ${report.changedBetCount ?? 0}, дельта ${report.scoreDelta ?? 0}`;
  }
}
